/**
 * PWA kaydı ve güncelleme akışı — Rapor 14 §10.
 *
 * ### ⚠️ Toplantı sırasında GÜNCELLEME YOK
 * Service Worker `skipWaiting()`'i **kendiliğinden çağırmaz**. Yeni sürüm
 * `waiting` durumunda bekler; kullanıcıya bir bildirim gösterilir ve
 * güncelleme **yalnız kullanıcı onayıyla ve toplantıda değilken** uygulanır.
 *
 * Aksi hâlde görüşmenin ortasında sayfa yeniden yüklenir — medya kopar,
 * WebSocket düşer, katılımcı listeden çıkar. Bu, "PWA ekledik" diye kabul
 * edilebilecek bir bedel değildir.
 */

export type UpdateState = "idle" | "available" | "applying";

export interface PwaHandle {
  /** Bekleyen bir güncelleme var mı? */
  readonly state: UpdateState;
  /** Kullanıcı onayıyla güncellemeyi uygular ve sayfayı yeniler. */
  applyUpdate(): void;
  dispose(): void;
}

/**
 * @param onUpdateAvailable Yeni sürüm hazır olduğunda çağrılır. Arayüz bunu
 *   **kalıcı ama engelleyici olmayan** bir bant olarak gösterir.
 * @param isBusy Toplantıda mıyız? `true` iken güncelleme **hiç önerilmez**.
 */
export function registerPwa(onUpdateAvailable: () => void, isBusy: () => boolean): PwaHandle {
  let state: UpdateState = "idle";
  let waiting: ServiceWorker | null = null;
  let registration: ServiceWorkerRegistration | null = null;

  const noop: PwaHandle = {
    get state() {
      return "idle" as const;
    },
    applyUpdate() {},
    dispose() {},
  };

  // ⚠️ Service Worker yalnız güvenli bağlamda (HTTPS veya localhost) çalışır.
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return noop;

  const announce = (sw: ServiceWorker): void => {
    waiting = sw;
    state = "available";
    // Toplantıdayken kullanıcıyı rahatsız etme; oda ekranı çıkışta yeniden sorar.
    if (!isBusy()) onUpdateAvailable();
  };

  void navigator.serviceWorker
    .register("/sw.js", { scope: "/" })
    .then((reg) => {
      registration = reg;
      if (reg.waiting) announce(reg.waiting);

      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener("statechange", () => {
          // `controller` varsa bu bir GÜNCELLEMEDİR, ilk kurulum değil.
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            announce(next);
          }
        });
      });
    })
    .catch(() => {
      // Kayıt başarısızlığı uygulamayı ETKİLEMEZ; PWA bir iyileştirmedir.
    });

  // Yeni SW kontrolü devraldığında **bir kez** yenile.
  let reloading = false;
  const onControllerChange = (): void => {
    if (reloading || state !== "applying") return;
    reloading = true;
    location.reload();
  };
  navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);

  return {
    get state() {
      return state;
    },
    applyUpdate() {
      if (!waiting) return;
      state = "applying";
      waiting.postMessage("centvox:skip-waiting");
    },
    dispose() {
      navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      void registration;
    },
  };
}

/**
 * ⚠️ **Gözlemlenebilirlik notu (Rapor 14 §12):** RUM ölçümü PWA'da farklıdır —
 * `display-mode: standalone` ile açılan oturumlar ayrı etiketlenir, aksi hâlde
 * "ilk boyama" metrikleri karışır (kabuk zaten önbellekte).
 */
export function displayMode(): "standalone" | "browser" {
  return window.matchMedia("(display-mode: standalone)").matches ? "standalone" : "browser";
}
